// @flow
import debounce from 'lodash.debounce';
import invariant from 'invariant';
import deepEqual from 'react-fast-compare';
import * as React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { trackEvent } from 'utils/analytics';
import { getParam } from 'utils/url';
import { listingSearchHome } from 'utils/searchHomePages';
import { isEmpty, objectEmpty } from 'utils/functional';
import getSearchUrl from 'utils/search/getSearchUrl';
import { fetchFeaturedJobs, selectFeaturedJob } from 'utils/featuredJobs';
import MainSearchResults from 'components/MainSearchResults';
import GradSearchResults from 'components/GradSearchResults';
import VolopsSearchResults from 'components/VolopsSearchResults';
import SearchLoadingSection from 'components/SearchLoadingSection';
import SaveSearchContainer from 'containers/SaveSearchContainer';
import type { Hit as SearchHit } from 'components/SearchHit';
import type { CurrentUser } from 'api/currentUser';
import getMainSearchTrackingData from 'utils/getMainSearchTrackingData';
import {
  isGradProgram,
  isVolunteering
} from 'utils/search/getSearchQueryOptions';
import type { Location, RouterHistory } from 'react-router';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import { results as resultsFn } from '../redux/main/reducers/mainSearch';
import {
  clearSearchLocation,
  fetchCurrentUser as fetchCurrentUserRaw
} from '../redux/main/actions/currentUser';
import {
  clearAllFilters,
  setSearchQuery,
  setSearchFilter,
  setPageIndex,
  search,
  selectAllSearchFilterOptions,
  selectSearchFilterOption,
  deselectSearchFilterOption
} from '../redux/main/actions/mainSearch';
import { pushFlashMessage } from '../redux/main/actions/flashMessages';

const TRACK_DISPLAYED_LISTING_WAIT = 500;

const trackDisplayedListings = (
  hits: Array<Object>,
  searchTrackingData: Object,
  featuredHits?: Array<Object>
) => {
  const listingIds = hits ? hits.map(h => h.objectID) : [];
  const rankingInfo = hits
    ? hits.map(({ _rankingInfo, objectID }) => ({
        rankingInfo: _rankingInfo,
        objectID
      }))
    : [];

  if (featuredHits) {
    trackEvent('Displayed Listings', {
      listing_ids: listingIds,
      promoted_listing_ids: featuredHits.map(h => h.objectID),
      ranking_info: rankingInfo,
      page_type: 'Searches for Listings',
      ...searchTrackingData
    });
  } else {
    trackEvent('Displayed Listings', {
      listing_ids: listingIds,
      ranking_info: rankingInfo,
      page_type: 'Searches for Listings',
      ...searchTrackingData
    });
  }
};

const debouncedTrackDisplayedListings = debounce(
  trackDisplayedListings,
  TRACK_DISPLAYED_LISTING_WAIT
);

const featuredJobIsAllowed = (job, hits, currentUser) =>
  !(
    hits.some(({ objectID }) => job.objectID === objectID) ||
    currentUser.orgs.some(org => org.id === job.orgID) ||
    currentUser.listingApplications.some(app => app.id === job.objectID)
  );

type OwnProps = {
  children: React.Node,
  content: Object,
  history: RouterHistory,
  location: Location,
  showListingCounts: boolean
};

type Props = {
  ...$Exact<OwnProps>,
  selectSearchFilterOption: Function,
  selectAllSearchFilterOptions: Function,
  deselectSearchFilterOption: Function,
  clearAllFilters: typeof clearAllFilters,
  clearSearchLocation: typeof clearSearchLocation,
  mainSearch: MainSearch,
  getSearchTrackingData: () => Object,
  modalShown: boolean,
  currentUser: CurrentUser & { isLoaded: boolean },
  searchRadius: string,
  search: Function,
  setSearchQuery: typeof setSearchQuery,
  setSearchFilter: Function,
  setPageIndex: Function,
  fetchCurrentUser: Function,
  defaultSearchType: ListingType,
  pushFlashMessage: typeof pushFlashMessage
};

type State = {
  featuredJob: ?SearchHit,
  featuredJobsLoading: boolean
};

class MainSearchContainer extends React.Component<Props, State> {
  state = {
    featuredJob: null,
    featuredJobsLoading: false
  };

  updateUrl(searchLocationHasChanged) {
    if (!this.props.currentUser.searchLocation) {
      return;
    }

    const {
      filters,
      searchFacets,
      query,
      pageIndex,
      sort,
      landingPagePath
    } = this.props.mainSearch;

    const originatingPathname = listingSearchHome(filters.type) || undefined;

    invariant(filters.type, 'Filter type required for main search url');
    const newUrl = getSearchUrl({
      filters,
      searchFacets,
      query,
      pageIndex: searchLocationHasChanged ? 0 : pageIndex,
      sort,
      location: this.props.currentUser.searchLocation,
      radius: this.props.searchRadius,
      resultsView: getParam(this.props.location, 'resultsView', true),
      landingPagePath,
      currentSearchParams: this.props.location.search,
      originatingPathname
    });

    if (
      newUrl.search === this.props.location.search &&
      newUrl.pathname === this.props.location.pathname
    ) {
      return;
    }
    this.props.history.replace(newUrl);
  }

  setFeaturedJob(hits) {
    const featuredJobId = selectFeaturedJob(
      hits.map(({ objectID }) => objectID)
    );
    const featuredJob = hits.find(({ objectID }) => objectID === featuredJobId);
    this.setState({
      featuredJob
    });
    return featuredJob;
  }

  trackResults(hits, featuredHits) {
    if (!this.props.modalShown && hits) {
      debouncedTrackDisplayedListings(
        hits,
        this.props.getSearchTrackingData(),
        featuredHits
      );
    }
  }

  updateFeaturedJobs(featuredHits, hits, query) {
    if (!(hits && hits.length)) {
      this.setState({ featuredJob: null, featuredJobsLoading: false });
      return;
    }

    let allowedJobs = featuredHits.filter(job =>
      featuredJobIsAllowed(job, hits, this.props.currentUser)
    );
    if (allowedJobs && allowedJobs.length) {
      const featuredJob = this.setFeaturedJob(allowedJobs);
      this.setState({ featuredJobsLoading: false });
      this.trackResults(hits, [featuredJob]);
    } else if (query && query.length && featuredHits.length === 0) {
      return fetchFeaturedJobs({
        query: '',
        searchLocation: this.props.currentUser.searchLocation,
        radius: this.props.searchRadius
      })
        .then(res => {
          allowedJobs = res.hits.filter(job =>
            featuredJobIsAllowed(job, hits, this.props.currentUser)
          );
          if (allowedJobs && allowedJobs.length) {
            const featuredJob = this.setFeaturedJob(allowedJobs);
            this.trackResults(hits, [featuredJob]);
          } else {
            this.setState({ featuredJob: null });
            this.trackResults(hits, []);
          }
        })
        .finally(() => {
          this.setState({ featuredJobsLoading: false });
        });
    } else {
      this.setState({ featuredJob: null });
      this.setState({ featuredJobsLoading: false });
      this.trackResults(hits, []);
    }
  }

  search() {
    if (
      this.props.currentUser.searchLocation &&
      this.props.currentUser.searchLocation.latitude &&
      this.props.mainSearch.filters.type === 'JOB'
    ) {
      const query = getParam(this.props.location, 'q', true);
      Promise.all([
        this.props.search(),
        fetchFeaturedJobs({
          query,
          searchLocation: this.props.currentUser.searchLocation,
          radius: this.props.searchRadius
        })
      ])
        .then(([results, featuredResults]) => {
          this.updateFeaturedJobs(featuredResults.hits, results.hits, query);
        })
        .catch(() => {
          this.setState({ featuredJobsLoading: false, featuredJob: null });
        });
      this.setState({ featuredJobsLoading: true });
    } else {
      this.props.search().then(results => {
        this.trackResults(results.hits);
      });
      this.setState({ featuredJob: null });
    }
  }

  componentDidMount() {
    if (
      this.props.mainSearch.initialized &&
      this.props.mainSearch.facetsThatArentLoaded.length === 0 &&
      this.props.currentUser.searchLocation
    ) {
      if (
        this.props.defaultSearchType &&
        this.props.mainSearch.filters.type !== this.props.defaultSearchType
      ) {
        this.props.setSearchFilter('type', this.props.defaultSearchType);
      } else {
        this.search();
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { mainSearch, searchRadius } = this.props;
    const prevMainSearch = prevProps.mainSearch;
    const prevRadius = prevProps.searchRadius;

    if (mainSearch.facetsThatArentLoaded.length > 0) return;

    const filtersHaveChanged = prevMainSearch.filters !== mainSearch.filters;
    const facetsHaveChanged =
      prevMainSearch.searchFacets !== mainSearch.searchFacets;

    const queryHasChanged =
      !isEmpty(this.props.location.search) &&
      getParam(this.props.location, 'q', true) !==
        getParam(prevProps.location, 'q', true);

    const typeHasChanged =
      prevMainSearch.filters.type !== mainSearch.filters.type;

    const isFirstSearch =
      mainSearch.facetsThatArentLoaded.length === 0 &&
      this.props.currentUser.searchLocation &&
      !mainSearch.initialSearchStarted &&
      !this.state.featuredJobsLoading;

    const searchParamsHaveChanged =
      mainSearch.facetsThatArentLoaded.length === 0 &&
      this.props.currentUser.searchLocation &&
      (prevMainSearch.useLocation !== mainSearch.useLocation ||
        prevProps.currentUser.searchLocation !==
          this.props.currentUser.searchLocation ||
        (prevMainSearch.pageIndex !== mainSearch.pageIndex &&
          !mainSearch.resultsByPage[mainSearch.pageIndex.toString()]));

    const radiusHasChanged = searchRadius !== prevRadius;
    const sortHasChanged =
      mainSearch.sort[mainSearch.filters.type] !==
      prevMainSearch.sort[mainSearch.filters.type];

    if (queryHasChanged) {
      this.props.setSearchQuery(
        getParam(this.props.location, 'q', true),
        false
      );
    }

    const prevPageParam = getParam(prevProps.location, 'page', true);
    const pageParam = getParam(this.props.location, 'page', true);
    const pageIndexParam = (parseInt(pageParam, 10) || 1) - 1;
    if (prevPageParam !== pageParam) {
      this.props.setPageIndex(pageIndexParam, true);
      return;
    }

    const searchLocationHasChanged = !deepEqual(
      this.props.currentUser.searchLocation,
      prevProps.currentUser.searchLocation
    );

    const pageHasChanged = prevMainSearch.pageIndex !== mainSearch.pageIndex;

    if (
      (this.props.content &&
        (this.props.content.onlySearchResults ||
          (this.props.content.heroSearch &&
            this.props.content.heroSearch.onlyResults &&
            !mainSearch.searchMode))) ||
      (isEmpty(this.props.location.search) &&
        ((filtersHaveChanged && !typeHasChanged) || facetsHaveChanged)) ||
      (!isEmpty(this.props.location.search) &&
        ((filtersHaveChanged && !typeHasChanged) ||
          facetsHaveChanged ||
          pageHasChanged ||
          typeHasChanged ||
          radiusHasChanged ||
          prevMainSearch.query !== mainSearch.query ||
          searchLocationHasChanged ||
          prevMainSearch.pageIndex !== mainSearch.pageIndex))
    ) {
      this.updateUrl(searchLocationHasChanged);
    }

    if (
      (mainSearch.facetsThatArentLoaded.length === 0 && filtersHaveChanged) ||
      queryHasChanged ||
      radiusHasChanged ||
      sortHasChanged ||
      searchParamsHaveChanged ||
      isFirstSearch
    ) {
      this.search();
    } else {
      const { pageIndex: prevPageIndex } = prevMainSearch;
      const { pageIndex, resultsByPage } = mainSearch;

      if (
        prevPageIndex === pageIndex &&
        prevPageParam !== pageParam &&
        pageIndexParam !== pageIndex
      ) {
        this.props.setPageIndex(pageIndexParam, true);
      }

      if (objectEmpty(resultsByPage)) {
        return;
      }
    }
    if (!prevProps.currentUser.isLoaded && this.props.currentUser.isLoaded) {
      this.props.fetchCurrentUser();
    }
  }

  render() {
    const scrollToId =
      this.props.location.state && this.props.location.state.scrollToId;
    const { mainSearch, currentUser, fetchCurrentUser } = this.props;
    const updateContactedGradPrograms = fetchCurrentUser;
    return resultsFn(mainSearch) ? (
      isGradProgram(mainSearch.filters.type) ? (
        <GradSearchResults
          {...this.props}
          updateContactedGradPrograms={updateContactedGradPrograms}
        />
      ) : isVolunteering(mainSearch.filters.type) ? (
        <SaveSearchContainer
          render={({ saveSearch, currentSearchIsSaved, saveableSearch }) => (
            <VolopsSearchResults
              {...this.props}
              scrollToId={scrollToId}
              searchLocation={currentUser.searchLocation}
              selectSearchFilterOption={this.props.selectSearchFilterOption}
              selectAllSearchFilterOptions={
                this.props.selectAllSearchFilterOptions
              }
              deselectSearchFilterOption={this.props.deselectSearchFilterOption}
              saveSearch={saveSearch}
              currentSearchIsSaved={currentSearchIsSaved}
              saveableSearch={saveableSearch}
              currentUser={currentUser}
            />
          )}
        />
      ) : (
        <SaveSearchContainer
          render={({ saveSearch, currentSearchIsSaved, saveableSearch }) => (
            <MainSearchResults
              {...this.props}
              searchLocation={currentUser.searchLocation}
              scrollToId={scrollToId}
              saveSearch={saveSearch}
              currentSearchIsSaved={currentSearchIsSaved}
              saveableSearch={saveableSearch}
              pushFlashMessage={this.props.pushFlashMessage}
              setSearchFilter={this.props.setSearchFilter}
              currentUser={currentUser}
              featuredJob={this.state.featuredJob}
              featuredJobsLoading={this.state.featuredJobsLoading}
            />
          )}
        />
      )
    ) : (
      <SearchLoadingSection hideLabel />
    );
  }
}

const mapStateToProps = state => {
  const {
    mainSearch,
    searchRadius: { value },
    currentUser,
    modal: { type }
  } = state;
  return {
    searchRadius: value,
    mainSearch,
    getSearchTrackingData: () => getMainSearchTrackingData(state),
    modalShown: !!type,
    currentUser
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    {
      clearSearchLocation,
      clearAllFilters,
      search,
      setSearchQuery,
      setSearchFilter,
      setPageIndex,
      fetchCurrentUser: fetchCurrentUserRaw,
      pushFlashMessage,
      selectSearchFilterOption,
      selectAllSearchFilterOptions,
      deselectSearchFilterOption
    }
  )(MainSearchContainer)
);
